/**
 * operationMerger.ts
 *
 * Merges multiple pending syncQueue operations for the same (noteId, parentId)
 * pair into a single BlockChangesPayload before sending to the API.
 *
 * Why grouping by BOTH noteId AND parentId matters:
 *   Nested structures (columnLayout → columnItem → paragraph, or
 *   bulletList → listItem → paragraph) each have a different parentId.
 *   The batch-update endpoint uses parentId to locate the parent record —
 *   if all blocks were merged into one call with a single parentId, the
 *   server would corrupt the parentId of every block whose real parent
 *   is a nested container rather than the page itself.
 *
 *   Grouping by (noteId + parentId) means each unique parent gets its own
 *   batch-update call with the correct parentId:
 *     - columnLayout  (parentId: pageId)          → one API call
 *     - columnItem    (parentId: columnLayoutId)   → separate API call
 *     - paragraph     (parentId: columnItemId)     → separate API call
 *
 *   Within each group, rapid edits are still collapsed:
 *     - creates: de-duplicated by blockId (keep first)
 *     - updates: de-duplicated by blockId (keep LATEST — highest createdAt)
 *     - deletes: de-duplicated by blockId
 *     - move:    keep LATEST only
 */

import type { SyncQueueRecord } from "../db/schema";
import type {
  BlockChangesPayload,
  CreateBlockPayload,
  UpdateBlockPayload,
  DeleteBlockPayload,
} from "@/types/block";

/**
 * Merges all BLOCK_CHANGES ops for one (noteId, parentId) group into a single payload.
 * Ops must be sorted by createdAt ascending before calling this.
 *
 * @param ops   All pending syncQueue records for a single (noteId, parentId) group, sorted oldest-first
 * @returns     One merged BlockChangesPayload ready to POST to the API
 */
export function mergeBlockOps(ops: SyncQueueRecord[]): BlockChangesPayload | null {
  if (ops.length === 0) return null;

  // Use metadata from the first op — all ops in the group share noteId, workspaceId, parentId
  const firstOp = ops[0]!;
  const basePayload = firstOp.payload as BlockChangesPayload;

  // Accumulate across all ops
  const createsMap = new Map<string, { payload: CreateBlockPayload; createdAt: number }>();
  const updatesMap = new Map<string, { payload: UpdateBlockPayload; createdAt: number }>();
  const deletesSet = new Set<string>();
  let latestMove: { blockIdArray: string[] } | null = null;
  let latestMoveAt = 0;

  for (const op of ops) {
    const payload = op.payload as BlockChangesPayload;

    // Creates — keep first occurrence only (don't re-create what's already created)
    for (const create of payload.creates ?? []) {
      if (!createsMap.has(create.blockId)) {
        createsMap.set(create.blockId, { payload: create, createdAt: op.createdAt });
      }
    }

    // Updates — keep LATEST for each blockId (most recent content wins)
    for (const update of payload.updates ?? []) {
      const existing = updatesMap.get(update.blockId);
      if (!existing || op.createdAt > existing.createdAt) {
        updatesMap.set(update.blockId, { payload: update, createdAt: op.createdAt });
      }
    }

    // Deletes — accumulate all unique blockIds
    for (const del of payload.deletes ?? []) {
      deletesSet.add(del.blockId);
    }

    // Move — keep LATEST only
    if (payload.move && op.createdAt > latestMoveAt) {
      latestMove = payload.move;
      latestMoveAt = op.createdAt;
    }
  }

  // If a block was created AND deleted in the same batch, skip both
  // (it never needs to touch MongoDB at all)
  const deletedBlockIds = new Set(deletesSet);
  const finalCreates: CreateBlockPayload[] = [];
  for (const [blockId, { payload }] of createsMap) {
    if (!deletedBlockIds.has(blockId)) {
      finalCreates.push(payload);
    }
  }

  // Remove updates for deleted blocks too
  const finalUpdates: UpdateBlockPayload[] = [];
  for (const [blockId, { payload }] of updatesMap) {
    if (!deletedBlockIds.has(blockId) && !createsMap.has(blockId)) {
      // Don't include update if this block is also being created
      // (the create payload already includes the content)
      finalUpdates.push(payload);
    }
  }

  const finalDeletes: DeleteBlockPayload[] = [];
  for (const blockId of deletesSet) {
    if (!createsMap.has(blockId)) {
      // Only send delete if block actually exists on server (i.e., was previously synced)
      finalDeletes.push({ blockId });
    }
  }

  return {
    noteId: basePayload.noteId,
    workspaceId: basePayload.workspaceId,
    parentId: basePayload.parentId,
    creates: finalCreates,
    updates: finalUpdates,
    deletes: finalDeletes,
    move: latestMove,
  };
}

/**
 * Groups a flat list of syncQueue records by (noteId + parentId).
 * Returns a Map of composite key → sorted ops (oldest first).
 *
 * Grouping by parentId as well as noteId ensures nested blocks
 * (e.g. columnLayout, columnItem, paragraph) each get their own
 * batch-update call with the correct parentId, preventing the server
 * from overwriting a nested block's parentId with the page-level one.
 *
 * @param ops   All unsynced BLOCK_CHANGES ops from syncQueue, in any order
 */
export function groupOpsByNoteId(
  ops: SyncQueueRecord[]
): Map<string, SyncQueueRecord[]> {
  const groups = new Map<string, SyncQueueRecord[]>();

  for (const op of ops) {
    const payload = op.payload as BlockChangesPayload;
    // Composite key: noteId + parentId — two ops only merge if BOTH match.
    // Null-safe: replace null/undefined with empty string so the key is always a string.
    const key = `${op.noteId ?? ''}::${payload.parentId ?? ''}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(op);
  }

  // Sort each group oldest-first so mergeBlockOps sees creates before updates
  for (const [, group] of groups) {
    group.sort((a, b) => a.createdAt - b.createdAt);
  }

  return groups;
}
